/**
 * FleetManager V3 - Driver Availability Widget
 * Shows operational availability of drivers at a glance
 * Clickable to navigate to drivers view
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Users, ChevronRight, UserCheck, UserMinus, UserX, AlertTriangle } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '../../shared/index.js';

export function DriverAvailabilityWidget({ data = {}, onNavigate }) {
  const { t } = useTranslation();
  const {
    total = 0,
    available = 0,
    unavailablePlanned = 0,
    unavailableUnplanned = 0,
    unknown = 0
  } = data;

  const unavailableTotal = unavailablePlanned + unavailableUnplanned;

  const segments = [
    {
      label: t('dashboard.drivers.available'),
      value: available,
      color: 'bg-emerald-500',
      percent: total > 0 ? (available / total) * 100 : 0,
      icon: UserCheck
    },
    {
      label: t('dashboard.drivers.planned'),
      value: unavailablePlanned,
      color: 'bg-amber-500',
      percent: total > 0 ? (unavailablePlanned / total) * 100 : 0,
      icon: UserMinus
    },
    {
      label: t('dashboard.drivers.absent'),
      value: unavailableUnplanned,
      color: 'bg-red-500',
      percent: total > 0 ? (unavailableUnplanned / total) * 100 : 0,
      icon: UserX
    }
  ];

  const handleClick = () => {
    onNavigate?.('drivers');
  };

  return (
    <Card
      className="cursor-pointer hover:ring-1 hover:ring-amber-500/50 transition-all"
      onClick={handleClick}
    >
      <CardHeader>
        <CardTitle icon={Users} iconColor="text-amber-500">{t('dashboard.drivers.title')}</CardTitle>
        <div className="flex items-center gap-1">
          <span className="text-lg font-bold text-gray-900 dark:text-white">{total}</span>
          <ChevronRight size={14} className="text-gray-400 dark:text-slate-500" />
        </div>
      </CardHeader>

      {/* Alert for unknown status */}
      {unknown > 0 && (
        <div className="mb-3 px-2 py-1.5 bg-gray-100 dark:bg-slate-700 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-gray-500 dark:text-slate-400" />
          <span className="text-xs text-gray-600 dark:text-slate-300">
            {t('dashboard.drivers.unknownStatus', { count: unknown })}
          </span>
        </div>
      )}

      {/* Progress bar */}
      <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded-full flex overflow-hidden mb-3">
        {segments.map((seg, i) => (
          <div
            key={i}
            className={`${seg.color} transition-all`}
            style={{ width: `${seg.percent}%` }}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="grid grid-cols-3 gap-2 text-center">
        {segments.map((seg, i) => {
          const Icon = seg.icon;
          return (
            <div key={i}>
              <div className="flex items-center justify-center gap-1">
                <Icon className={`w-3 h-3 ${
                  seg.color === 'bg-emerald-500' ? 'text-emerald-500' :
                  seg.color === 'bg-amber-500' ? 'text-amber-500' :
                  'text-red-500'
                }`} />
                <span className="font-semibold text-gray-900 dark:text-white text-sm">{seg.value}</span>
              </div>
              <span className="text-xs text-gray-500 dark:text-slate-400">{seg.label}</span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default DriverAvailabilityWidget;
